import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UserCog,
  Lock,
  Languages,
  ChevronRight,
  LogOut,
  Mail,
  Phone,
  X,
  Eye,
  EyeOff,
  Check,
  Loader2,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useMe } from "../hooks/queries/useMe";
import { useChangePassword } from "../hooks/queries/useAuthMutations";
import { useNotifications } from "../components/layout/routing/AppShell";
import { formatHeight } from "../utils/formatHeight";
import AcademySharingSetting from "../features/reports/AcademySharingSetting";
import AcademyMembershipCard from "../features/academy/AcademyMembershipCard";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.4, staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

// Fila táctil del bloque "Ajustes de cuenta". Misma estructura que las filas de
// la tarjeta "Seguridad y Cuenta" del scout (badge + texto + chevron).
const AccountRow = ({ icon: Icon, badgeClass, label, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center justify-between rounded-lg p-3 text-left transition-colors hover:bg-gray-50"
  >
    <div className="flex items-center gap-3">
      <div className={`rounded-lg p-2 ${badgeClass}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-sm font-bold text-gray-900">{label}</p>
        {description && (
          <p className="text-xs text-gray-500">{description}</p>
        )}
      </div>
    </div>
    <ChevronRight size={18} className="text-gray-400" />
  </button>
);

const SettingsPage = () => {
  const navigate = useNavigate();
  const { userRole, athleteData, scoutData, signOut } = useAuth();
  const me = useMe();
  const changePassword = useChangePassword();
  const { openNotifications } = useNotifications();

  const isAthlete = userRole === "athlete";
  const profile = isAthlete ? athleteData : scoutData;
  const user = me.data?.user ?? null;

  const displayName =
    profile?.full_name || user?.full_name || "Usuario";
  const email = user?.email || profile?.email || null;
  const phone = profile?.phone || null;
  const roleLabel = isAthlete ? "Atleta" : "Coach";

  // Avatar: athletes lo guardan en attributes.avatar_url; los scouts en
  // details.public_profile.avatar_url (o details.avatar_url). Sin foto, caemos
  // a la inicial del nombre.
  const scoutDetails = scoutData?.details || {};
  const avatarUrl = isAthlete
    ? athleteData?.attributes?.avatar_url || null
    : scoutDetails.public_profile?.avatar_url || scoutDetails.avatar_url || null;
  const initial = displayName.trim().charAt(0).toUpperCase() || "U";

  // Stats sólo para atletas y sólo si hay algún dato real que mostrar.
  const rawAttrs = athleteData?.attributes || {};
  const athleteStats = isAthlete
    ? [
        rawAttrs.weight ? { label: "Peso", value: `${rawAttrs.weight} kg` } : null,
        rawAttrs.height
          ? { label: "Altura", value: formatHeight(rawAttrs.height) }
          : null,
        rawAttrs.age ? { label: "Edad", value: `${rawAttrs.age} años` } : null,
      ].filter(Boolean)
    : [];

  const profilePath = isAthlete ? "/athlete/profile/personal" : "/scout/profile";

  // ---- modal de cambio de contraseña (reusa useChangePassword) --------------
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);

  const passwordChecks = [
    { label: "Al menos 8 caracteres", ok: newPassword.length >= 8 },
    {
      label: "Incluye letras y números",
      ok: /[a-zA-Z]/.test(newPassword) && /\d/.test(newPassword),
    },
    {
      label: "Coincide con la confirmación",
      ok: Boolean(newPassword) && newPassword === confirmPassword,
    },
    {
      label: "Distinta a la actual",
      ok: Boolean(newPassword) && newPassword !== currentPassword,
    },
  ];
  const isPasswordValid =
    Boolean(currentPassword) && passwordChecks.every((c) => c.ok);

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordMessage(null);
  };

  const handleChangePassword = async () => {
    if (!isPasswordValid || changePassword.isPending) return;
    setPasswordMessage(null);
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setPasswordMessage({ kind: "success", text: "Contraseña actualizada." });
      setTimeout(closePasswordModal, 900);
    } catch (error) {
      setPasswordMessage({
        kind: "error",
        text: error?.message || "No se pudo actualizar la contraseña.",
      });
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <main className="mx-auto w-full max-w-3xl p-4 sm:p-6">
        <motion.div
          className="space-y-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.h1
            variants={itemVariants}
            className="text-2xl font-semibold text-gray-900"
          >
            Configuración
          </motion.h1>

          {/* Cabecera de perfil */}
          <motion.div
            variants={itemVariants}
            className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm"
          >
            <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-center sm:text-left">
              <div className="flex-shrink-0">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={displayName}
                    className="h-20 w-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blue-900 text-2xl font-bold text-white">
                    {initial}
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-col items-center gap-2 sm:flex-row">
                  <h2 className="truncate text-xl font-bold text-blue-900">
                    {displayName}
                  </h2>
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-blue-700">
                    {roleLabel}
                  </span>
                </div>

                <div className="mt-2 space-y-1">
                  {email && (
                    <p className="flex items-center justify-center gap-2 text-sm text-gray-600 sm:justify-start">
                      <Mail size={15} className="text-gray-400" />
                      <span className="truncate">{email}</span>
                    </p>
                  )}
                  {phone && (
                    <p className="flex items-center justify-center gap-2 text-sm text-gray-600 sm:justify-start">
                      <Phone size={15} className="text-gray-400" />
                      <span className="truncate">{phone}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Stats del atleta (Peso / Altura / Edad) */}
            {athleteStats.length > 0 && (
              <div className="mt-6 grid grid-cols-3 gap-3 border-t border-gray-100 pt-4">
                {athleteStats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="mb-1 text-xs text-gray-500">{stat.label}</p>
                    <p className="font-semibold text-gray-900">{stat.value}</p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Mi academia: el atleta pide unirse; un coach verificado confirma */}
          {isAthlete && (
            <motion.div
              variants={itemVariants}
              className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm"
            >
              <h3 className="mb-2 text-lg font-bold text-blue-900">Mi academia</h3>
              <AcademyMembershipCard />
            </motion.div>
          )}

          {/* Privacidad: qué ve la academia en sus reportes */}
          {isAthlete && (
            <motion.div
              variants={itemVariants}
              className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm"
            >
              <h3 className="mb-2 text-lg font-bold text-blue-900">Privacidad</h3>
              <AcademySharingSetting athleteId={athleteData?.id} attributes={athleteData?.attributes} />
            </motion.div>
          )}

          {/* Ajustes de cuenta */}
          <motion.div
            variants={itemVariants}
            className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm"
          >
            <h3 className="mb-4 text-lg font-bold text-blue-900">
              Ajustes de cuenta
            </h3>

            <div className="space-y-2">
              <AccountRow
                icon={UserCog}
                badgeClass="bg-blue-100 text-blue-700"
                label="Editar perfil"
                description="Actualiza tus datos personales"
                onClick={() => navigate(profilePath)}
              />
              <AccountRow
                icon={Lock}
                badgeClass="bg-blue-100 text-blue-700"
                label="Cambiar contraseña"
                description="Actualiza tu contraseña de acceso"
                onClick={() => setIsPasswordModalOpen(true)}
              />
              <AccountRow
                icon={Mail}
                badgeClass="bg-amber-100 text-amber-700"
                label="Notificaciones"
                description="Revisa tus avisos recientes"
                onClick={openNotifications}
              />

              {/* Idioma: fila informativa (no hay i18n todavía). Sin chevron ni
                  hover para no sugerir una acción que no existe. */}
              <div className="flex items-center justify-between rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-purple-100 p-2 text-purple-700">
                    <Languages size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Idioma</p>
                    <p className="text-xs text-gray-500">
                      Idioma de la aplicación
                    </p>
                  </div>
                </div>
                <span className="text-sm font-medium text-gray-600">Español</span>
              </div>
            </div>
          </motion.div>

          {/* Cerrar sesión */}
          <motion.div variants={itemVariants}>
            <button
              type="button"
              onClick={signOut}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-100 bg-white px-6 py-3 font-bold text-red-600 shadow-sm transition-colors hover:bg-red-600 hover:text-white"
            >
              <LogOut size={18} />
              <span>Cerrar sesión</span>
            </button>
          </motion.div>
        </motion.div>
      </main>

      {/* Modal de cambio de contraseña */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-blue-900">
                Cambiar contraseña
              </h3>
              <button
                type="button"
                onClick={closePasswordModal}
                aria-label="Cerrar"
                className="grid h-8 w-8 place-items-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Contraseña actual"
                autoComplete="current-password"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nueva contraseña"
                  autoComplete="new-password"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  aria-label={
                    showNewPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                  }
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <input
                type={showNewPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmar nueva contraseña"
                autoComplete="new-password"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <ul className="space-y-1 pt-1">
                {passwordChecks.map((check) => (
                  <li
                    key={check.label}
                    className={`flex items-center gap-2 text-xs ${
                      check.ok ? "text-green-600" : "text-gray-400"
                    }`}
                  >
                    <Check size={14} />
                    <span>{check.label}</span>
                  </li>
                ))}
              </ul>

              {passwordMessage && (
                <p
                  className={`text-sm font-medium ${
                    passwordMessage.kind === "success"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {passwordMessage.text}
                </p>
              )}

              <button
                type="button"
                onClick={handleChangePassword}
                disabled={!isPasswordValid || changePassword.isPending}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-900 px-6 py-2.5 font-bold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {changePassword.isPending && (
                  <Loader2 size={16} className="animate-spin" />
                )}
                <span>Actualizar contraseña</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
